import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, MapPin, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import type { CompanyListItem } from '../../lib/api';

interface GroupMemberCompaniesCardProps {
  companies: CompanyListItem[];
}

/**
 * Searchable list of companies belonging to a customer group.
 */
export function GroupMemberCompaniesCard({ companies }: GroupMemberCompaniesCardProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((company) => {
      const haystack = [
        company.name,
        company.city,
        company.country,
        company.primary_email,
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [companies, search]);

  return (
    <Card className="h-full">
      <CardHeader className="space-y-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Member Companies
          </CardTitle>
          <CardDescription>
            {companies.length} {companies.length === 1 ? 'company' : 'companies'} in this group
          </CardDescription>
        </div>
        {companies.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="pl-9"
              aria-label="Search member companies"
            />
          </div>
        )}
      </CardHeader>
      <CardContent>
        {companies.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No companies in this group yet.
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No companies match &quot;{search}&quot;.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((company) => {
              const location = [company.city, company.country].filter(Boolean).join(', ');
              return (
                <Link
                  key={company.id}
                  to={`/customers/${company.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-sm">{company.name}</p>
                      <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {location || 'No location'}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="outline" className="hidden sm:inline-flex text-xs">
                      {company.user_count} {company.user_count === 1 ? 'user' : 'users'}
                    </Badge>
                    {company.site_count > 0 && (
                      <Badge variant="outline" className="hidden md:inline-flex text-xs">
                        {company.site_count} {company.site_count === 1 ? 'site' : 'sites'}
                      </Badge>
                    )}
                    <Badge className={company.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
